import { defineStore } from "pinia";
import type { Communication } from "~/types/communication";

export const useCommunicationStore = defineStore("communication", () => {
  const auth = useAuthStore();
  const toast = useToast();

  // ============= STATE =============
  const count = useState<number>("communication.count", () => 0);
  const communicationsById = reactive(new Map<string, Communication>());
  const idsByType = reactive(new Map<string, string[]>());
  const loading = ref(false);
  const selectedCommunicationId = ref<string | null>(null);
  const selectedCommunication = ref<Communication | null>(null);

  // ============= GETTERS =============

  const getCommunicationsByType = (type: string): Communication[] => {
    const ids = idsByType.get(type) || [];
    return ids
      .map((id) => communicationsById.get(id))
      .filter((c): c is Communication => c !== undefined);
  };

  const getCommunications = (): Communication[] => {
    return Array.from(communicationsById.values());
  };

  const getCommunication = (id: string): Communication | undefined => {
    return communicationsById.get(id);
  };

  // ============= ACTIONS =============

  const setCommunication = (communication: Communication) => {
    communicationsById.set(communication.id, communication);
  };

  const addIdToType = (type: string, id: string) => {
    const currentIds = idsByType.get(type) || [];
    if (!currentIds.includes(id)) {
      idsByType.set(type, [id, ...currentIds]); // Prepend new
    }
  };

  const fetchCount = async () => {
    try {
      const response = await auth.api<{ count: number }>("get", "/communications/count");
      count.value = response.count;
      return response.count;
    } catch (error: any) {
      if (error?.statusCode === 401) {
        count.value = 0;
        return 0;
      }
      throw error;
    }
  };

  const fetchCommunications = async (type?: "alert" | "notif" | "all") => {
    loading.value = true;
    try {
      const queryParams: any = {};
      if (type) queryParams.type = type;

      const response = await auth.api<{ data: Communication[] }>("get", "/communications", {
        queryString: queryParams,
      });

      // Handle potential pagination structure (response.data or response.data.data)
      // If auth.api returns the body, and body has { data: [...] }
      const communications = Array.isArray(response.data)
        ? response.data
        : (response as any).data?.data || [];

      const ids: string[] = [];

      // console.log("communications", communications);
      if (Array.isArray(communications)) {
        communications.forEach((comm: Communication) => {
          if (comm && comm.id) {
            setCommunication(comm);
            ids.push(comm.id);
          }
        });
      }

      // Replace ids for this type (or 'all' if no type specified)
      const key = type || "all";
      idsByType.set(key, ids);
    } catch (error: any) {
      toast.add({
        title: "Error",
        description: "Failed to fetch communications",
        color: "error",
      });
    } finally {
      loading.value = false;
    }
  };

  const fetchFeaturedAlert = async (groupId: number): Promise<Communication | null> => {
    try {
      const response = await auth.api<Communication | null>(
        "get",
        `/communications/featured/${groupId}`,
      );
      // If there's a featured alert, store it
      if (response && response.id) {
        setCommunication(response);
        return response;
      }

      return null;
    } catch (error: any) {
      console.error("Failed to fetch featured alert:", error);
      return null;
    }
  };

  const createCommunication = async (data: Partial<Communication>) => {
    try {
      const response = await auth.api<Communication>("post", "/communications", data);
      if (response && response.id) {
        setCommunication(response);
      }
      return response;
    } catch (error: any) {
      console.error("Failed to create communication:", error);
      throw error;
    }
  };

  const updateCommunication = async (id: string, data: Partial<Communication>) => {
    let existingComm: Communication | undefined;
    try {
      // Optimistically update in store
      existingComm = communicationsById.get(id);
      if (existingComm) {
        communicationsById.set(id, { ...existingComm, ...data });
      }

      // Make API call
      const response = await auth.api<Communication>("put", `/communications/${id}`, data);

      // Update store with response
      if (response && response.id) {
        setCommunication(response);
      }

      return response;
    } catch (error: any) {
      // Rollback on error
      if (existingComm) {
        communicationsById.set(id, existingComm);
      }
      console.error("Failed to update communication:", error);
      throw error;
    }
  };

  const setSelectedCommunication = (id: string | null) => {
    selectedCommunicationId.value = id;
    if (id) {
      const comm = communicationsById.get(id);
      selectedCommunication.value = comm || null;
    } else {
      selectedCommunication.value = null;
    }
  };

  // Watch selectedCommunicationId and communicationsById to update selectedCommunication
  // Use a computed to get all communications for reactivity tracking
  const allCommunications = computed(() => Array.from(communicationsById.values()));

  watch(
    [selectedCommunicationId, allCommunications],
    ([id, comms]) => {
      if (id) {
        const comm = comms.find((c) => c.id === id) || communicationsById.get(id);
        selectedCommunication.value = comm || null;
      } else {
        selectedCommunication.value = null;
      }
    },
    { immediate: true },
  );

  const markAsArchived = async (id: string) => {
    const comm = communicationsById.get(id);
    if (!comm) return;

    // Store type for toast message before deletion
    const commType = comm.type;

    // Optimistic update
    comm.archived_at = new Date().toISOString();
    setCommunication(comm);

    try {
      await auth.api<Communication>("put", `/communications/${id}`, {
        archived_at: new Date().toISOString(),
      });

      // Show success toast
      toast.add({
        description:
          commType === "alert"
            ? "This alert has been moved to your archive."
            : "This notification has been moved to your archive.",
        color: "success",
        icon: "i-heroicons-archive-box-arrow-down",
      });
    } catch (error: any) {
      // Revert
      comm.archived_at = null;
      setCommunication(comm);
      toast.add({
        title: "Error",
        description: "Failed to update status",
        color: "error",
      });
    } finally {
      communicationsById.delete(id);
      count.value--;
    }
  };

  const markAsRead = async (id: string) => {
    const comm = communicationsById.get(id);
    if (!comm) return;

    // Optimistic update
    const previousReadAt = comm.read_at;
    comm.read_at = new Date().toISOString();
    setCommunication(comm);
    count.value--;
    try {
      await auth.api<Communication>("put", `/communications/${id}`, { read: true });
    } catch (error: any) {
      // Revert
      comm.read_at = previousReadAt;
      setCommunication(comm);
      toast.add({
        title: "Error",
        description: "Failed to update status",
        color: "error",
      });
    }
  };

  const markAsUnread = async (id: string) => {
    const comm = communicationsById.get(id);
    if (!comm) return;

    // Optimistic update
    const previousReadAt = comm.read_at;
    comm.read_at = null;
    setCommunication(comm);
    count.value++;
    try {
      await auth.api<Communication>("put", `/communications/${id}`, { unread: true });
    } catch (error: any) {
      // Revert
      comm.read_at = previousReadAt;
      setCommunication(comm);
      toast.add({
        title: "Error",
        description: "Failed to update status",
        color: "error",
      });
    }
  };

  const toggleFeatured = async (id: string, featured: boolean) => {
    const comm = communicationsById.get(id);
    if (!comm) return;

    // Optimistic update
    const previousFeatured = comm.featured;
    comm.featured = featured;
    setCommunication(comm);

    try {
      await auth.api<Communication>("put", `/communications/${id}`, { featured });
      toast.add({
        title: "Success",
        description: featured ? "Alert featured" : "Alert unfeatured",
        icon: "i-lucide-concierge-bell",
        color: "success",
      });
    } catch (error: any) {
      // Revert
      comm.featured = previousFeatured;
      setCommunication(comm);
      toast.add({
        title: "Error",
        description: "Failed to update featured status",
        icon: "i-lucide-concierge-bell",
        color: "error",
      });
    }
  };

  const deleteCommunication = async (id: string) => {
    const comm = communicationsById.get(id);
    if (!comm) return;

    // Optimistic remove
    communicationsById.delete(id);
    const typeIds = idsByType.get(comm.type) || [];
    idsByType.set(
      comm.type,
      typeIds.filter((i) => i !== id),
    );

    try {
      await auth.api("delete", `/communications/${id}`);
      toast.add({
        title: "Success",
        description: "Communication deleted",
        color: "success",
      });
    } catch (error: any) {
      // Revert (simplified: just fetch again or warn)
      toast.add({
        title: "Error",
        description: "Failed to delete communication",
        color: "error",
      });
      // Ideally we should restore it, but for now fetching again is safer/easier
      fetchCommunications(comm.type);
    }
  };

  /**
   * Add a communication locally from an SSE event
   * Used when receiving real-time notifications via Server-Sent Events
   */
  const addCommunicationLocal = (communication: Communication) => {
    // Add to store
    setCommunication(communication);

    // Add ID to the appropriate type list (prepend so it appears first)
    addIdToType(communication.type, communication.id);

    // Also add to 'all' list
    addIdToType("all", communication.id);

    // Increment unread count if the notification is unread
    if (!communication.read_at) {
      count.value++;
    }
  };

  return {
    communicationsById,
    loading,
    selectedCommunicationId,
    selectedCommunication,
    getCommunications,
    getCommunicationsByType,
    getCommunication,
    count,

    fetchCount,
    fetchCommunications,
    fetchFeaturedAlert,
    createCommunication,
    updateCommunication,
    setSelectedCommunication,
    addCommunicationLocal,

    markAsRead,
    markAsUnread,
    markAsArchived,
    toggleFeatured,
    deleteCommunication,
  };
});
